"use client";
import { API_URL } from "@/lib/config";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FolderPlus, FolderOpen, Loader2, ArrowLeft, ExternalLink, Trash2, ImageOff } from "lucide-react";
import { getCollectionsApiCollectionsGet, createCollectionApiCollectionsPost, CollectionResponse } from "@/api-client";
import { motion, AnimatePresence } from "framer-motion";

const API = API_URL;

interface PanoramaItem {
  id: string;
  name?: string;
  base_tile_url?: string;
  capture_date?: string;
  creator_name?: string;
  view_count?: number;
  average_rating?: number;
}

interface CollectionsModalProps {
  onClose: () => void;
}

export default function CollectionsModal({ onClose }: CollectionsModalProps) {
  const [collections, setCollections] = useState<CollectionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newColName, setNewColName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Detail view
  const [selectedCollection, setSelectedCollection] = useState<CollectionResponse | null>(null);
  const [panoramas, setPanoramas] = useState<PanoramaItem[]>([]);
  const [isPanosLoading, setIsPanosLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    loadCollections();
  }, []);

  async function loadCollections() {
    setIsLoading(true);
    try {
      const { data } = await getCollectionsApiCollectionsGet();
      if (data) setCollections(data);
    } catch (err) {
      console.error("Failed to load collections", err);
    } finally {
      setIsLoading(false);
    }
  }

  async function openCollection(col: CollectionResponse) {
    setSelectedCollection(col);
    setIsPanosLoading(true);
    try {
      const res = await fetch(`${API}/api/collections/${col.id}/panoramas`, { credentials: "include" });
      if (res.ok) {
        const data: PanoramaItem[] = await res.json();
        setPanoramas(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsPanosLoading(false);
    }
  }

  function goBack() {
    setSelectedCollection(null);
    setPanoramas([]);
  }

  async function handleRemove(panoramaId: string) {
    if (!selectedCollection) return;
    setRemovingId(panoramaId);
    try {
      await fetch(`${API}/api/collections/${selectedCollection.id}/panoramas/${panoramaId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setPanoramas((prev) => prev.filter((p) => p.id !== panoramaId));
      // Update count in parent list
      setCollections((prev) =>
        prev.map((c) =>
          c.id === selectedCollection.id
            ? { ...c, panorama_count: (c.panorama_count ?? 1) - 1 }
            : c
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setRemovingId(null);
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newColName.trim()) return;
    setIsCreating(true);
    try {
      const { data } = await createCollectionApiCollectionsPost({ body: { name: newColName } });
      if (data) {
        setCollections((prev) => [data, ...prev]);
        setNewColName("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 8 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        className="bg-tone-900 border border-ink/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/5">
          <div className="flex items-center gap-2">
            {selectedCollection && (
              <button onClick={goBack} className="text-tone-400 hover:text-ink transition-colors mr-1">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <FolderOpen className="w-5 h-5 text-indigo-400" />
            <h2 className="text-base font-bold text-ink">
              {selectedCollection ? selectedCollection.name : "My Collections"}
            </h2>
            {selectedCollection && (
              <span className="text-xs text-tone-500 ml-1">({panoramas.length} photos)</span>
            )}
          </div>
          <button onClick={onClose} className="text-tone-400 hover:text-ink transition-colors text-lg leading-none">✕</button>
        </div>

        <AnimatePresence mode="wait">

          {/* ── COLLECTION LIST VIEW ── */}
          {!selectedCollection && (
            <motion.div
              key="list"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.15 }}
              className="p-6"
            >
              <form onSubmit={handleCreate} className="flex gap-2 mb-5">
                <input
                  type="text"
                  placeholder="New collection name..."
                  className="flex-1 bg-ink/5 border border-ink/10 rounded-lg px-3 py-2 text-sm text-ink placeholder-tone-500 focus:outline-none focus:border-indigo-500 transition-colors"
                  value={newColName}
                  onChange={(e) => setNewColName(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={isCreating || !newColName.trim()}
                  className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors shrink-0"
                >
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
                  Create
                </button>
              </form>

              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/20">
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                  </div>
                ) : collections.length === 0 ? (
                  <p className="text-sm text-center text-tone-500 py-8">No collections yet. Create one above.</p>
                ) : (
                  collections.map((c) => (
                    <button
                      key={String(c.id)}
                      onClick={() => openCollection(c)}
                      className="w-full flex items-center justify-between p-3 bg-ink/5 hover:bg-ink/10 border border-ink/5 hover:border-indigo-500/30 rounded-xl transition-all group text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0">
                          <FolderOpen className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-tone-200 group-hover:text-ink">{c.name}</p>
                          <p className="text-xs text-tone-500">{c.panorama_count ?? 0} panoramas</p>
                        </div>
                      </div>
                      <span className="text-tone-600 group-hover:text-indigo-400 transition-colors text-lg">›</span>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* ── COLLECTION DETAIL VIEW ── */}
          {selectedCollection && (
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.15 }}
              className="p-6"
            >
              {isPanosLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                </div>
              ) : panoramas.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-tone-500">
                  <ImageOff className="w-10 h-10 opacity-30" />
                  <p className="text-sm">This collection is empty.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/20">
                  <AnimatePresence initial={false}>
                    {panoramas.map((pano) => {
                      const thumb = pano.base_tile_url
                        ? `${pano.base_tile_url}9/0_0.webp`
                        : null;
                      return (
                        <motion.div
                          key={pano.id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-center gap-3 p-2.5 bg-ink/5 hover:bg-ink/8 border border-ink/5 rounded-xl group"
                        >
                          {/* Thumbnail */}
                          <div className="w-14 h-10 rounded-lg overflow-hidden bg-tone-800 shrink-0">
                            {thumb ? (
                              <img src={thumb} alt={pano.name || ""} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageOff className="w-4 h-4 text-tone-600" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-tone-200 truncate">{pano.name || "Untitled"}</p>
                            <p className="text-xs text-tone-500 truncate">by {pano.creator_name || "Unknown"}</p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 shrink-0">
                            <a
                              href={`#pano=${pano.id}&zoom=30`}
                              onClick={onClose}
                              className="p-1.5 text-tone-500 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                              title="Open panorama"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => handleRemove(pano.id)}
                              disabled={removingId === pano.id}
                              className="p-1.5 text-tone-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                              title="Remove from collection"
                            >
                              {removingId === pano.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </motion.div>
    </div>
  );

  return createPortal(modal, document.body);
}
